import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import {
  setApiAuthHandlers,
  setAccessToken,
  webchatApi,
} from './api';
import {
  clearRecallProfile,
  saveRecallProfile,
} from './auth/emailAuth';

const AuthContext = createContext();

/**
 * The session restore must hit the server exactly once per page load: the
 * backend ROTATES the remember token on every successful restore, so two
 * concurrent calls (StrictMode's double effect, or any remount) make the
 * second arrive with a stale cookie — 401, cookie cleared, learner silently
 * logged out. Sharing one in-flight promise removes the race at the source.
 */
let inflightSessionRequest = null;
function fetchSessionOnce() {
  if (!inflightSessionRequest) {
    inflightSessionRequest = webchatApi.get('/auth/session/').finally(() => {
      inflightSessionRequest = null;
    });
  }
  return inflightSessionRequest;
}

// Written by older builds; cleared on logout so nothing stale survives an upgrade.
const CHANNEL_KEYS = ['channelUUID', 'web_channel_id', 'channel'];

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState('');
  const [username, setUsername] = useState('');
  const [bootstrapped, setBootstrapped] = useState(false);
  const [continuePrompt, setContinuePrompt] = useState(null);

  const applySession = useCallback((data) => {
    const access = data?.access || '';
    const user = data?.username || '';
    setAccessToken(access);
    setToken(access);
    setUsername(user);
    if (user) {
      saveRecallProfile({
        email: user,
        lastSeenAt: data?.last_seen_at || null,
      });
    }
  }, []);

  const clearSessionLocal = useCallback(() => {
    setAccessToken('');
    setToken('');
    setUsername('');
    setContinuePrompt(null);
    CHANNEL_KEYS.forEach((key) => localStorage.removeItem(key));
  }, []);

  const login = useCallback(
    (newToken, newUsername, meta = {}) => {
      applySession({
        access: newToken,
        username: newUsername,
        last_seen_at: meta.lastSeenAt || null,
      });
      setContinuePrompt(null);
    },
    [applySession]
  );

  const logout = useCallback(async () => {
    try {
      await webchatApi.post('/auth/logout/', {});
    } catch {
      /* still clear local session */
    }
    clearSessionLocal();
    clearRecallProfile();
  }, [clearSessionLocal]);

  const continueAs = useCallback(async () => {
    const response = await webchatApi.get('/auth/session/', {
      params: { continue: '1' },
    });
    applySession(response.data);
    setContinuePrompt(null);
    return response.data;
  }, [applySession]);

  const dismissContinue = useCallback(async () => {
    try {
      await webchatApi.post('/auth/logout/', {});
    } catch {
      /* ignore */
    }
    clearSessionLocal();
    clearRecallProfile();
  }, [clearSessionLocal]);

  useEffect(() => {
    setApiAuthHandlers({ onUnauthorized: () => {
      clearSessionLocal();
    }});
    return () => setApiAuthHandlers({ onUnauthorized: null });
  }, [clearSessionLocal]);

  useEffect(() => {
    let cancelled = false;

    const restore = async () => {
      try {
        const response = await fetchSessionOnce();
        if (cancelled) return;
        if (response.data?.require_continue_prompt && !response.data?.access) {
          setContinuePrompt(response.data);
          setUsername(response.data.username || '');
          setAccessToken('');
          setToken('');
        } else if (response.data?.access) {
          applySession(response.data);
        }
      } catch {
        if (!cancelled) {
          setAccessToken('');
          setToken('');
        }
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, [applySession]);

  return (
    <AuthContext.Provider
      value={{
        token,
        username,
        bootstrapped,
        continuePrompt,
        login,
        logout,
        continueAs,
        dismissContinue,
        setContinuePrompt,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
